import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../../../contexts/AppContext';
import FilterDrawer from './FilterDrawer';
import './ShopHeader.css';

interface ShopHeaderProps {
  showLimit: number;
  setShowLimit: (limit: number) => void;
  viewGrid: number;
  setViewGrid: (grid: number) => void;
  sortOption: string;
  setSortOption: (option: string) => void;
}

const ShopHeader: React.FC<ShopHeaderProps> = ({
  showLimit,
  viewGrid, setViewGrid,
  sortOption, setSortOption
}) => {
  const { productListSidebar, setProductListSidebar, filteredProducts } = useApp();
  const { selectedCategory, priceRange, selectedTag } = productListSidebar;
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const isFiltered =
    selectedCategory !== '' ||
    selectedTag !== '' ||
    priceRange.min !== 0 ||
    priceRange.max !== 1500;

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProductListSidebar(prev => ({
      ...prev,
      selectedCategory: '',
      selectedTag: '',
      priceRange: { min: 0, max: 1500 }
    }));
  };

  const totalResults = filteredProducts.length;
  const currentShow = Math.min(showLimit, totalResults);

  // Generate dynamic show limits based on total results
  // const baseLimits = [8, 16, 32];
  // const showOptions = totalResults > 0
  //   ? Array.from(new Set([...baseLimits.filter(l => l < totalResults), totalResults])).sort((a, b) => a - b)
  //   : [8];

  const sortOptions = [
    { value: 'default', label: 'Default Sorting' },
    { value: 'price', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
  ];

  const currentSortLabel = sortOptions.find(opt => opt.value === sortOption)?.label || 'Default Sorting';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="shop-header-container">
      <div className="shop-header-left">
        Showing 1–{currentShow} of {totalResults} results
      </div>

      <div className="shop-header-right">
        {/* <div className="shop-header-group">
          <span className="shop-header-label">Show</span>
          <div className="shop-header-options">
            {showOptions.map(limit => (
              <span
                key={limit}
                className={`shop-header-option ${showLimit === limit ? 'active' : ''}`}
                onClick={() => setShowLimit(limit)}
              >
                {limit}
              </span>
            ))}
          </div>
        </div> */}

        <div className="shop-header-separator"></div>
        <div className="shop-header-group">
          <span className="shop-header-label">View</span>
          <div className="shop-header-options">
            {[3, 4, 5].map(grid => (
              <span
                key={grid}
                className={`shop-header-option ${viewGrid === grid ? 'active' : ''}`}
                onClick={() => setViewGrid(grid)}
              >
                {grid}
              </span>
            ))}
          </div>
        </div>

        <div className="shop-header-separator"></div>

        <div className="shop-header-sorting" ref={sortRef} onClick={() => setShowSortDropdown(!showSortDropdown)} style={{ position: 'relative' }}>
          <span>{currentSortLabel}</span>
          <div className="chevron-icon"></div>

          {showSortDropdown && (
            <div className="sort-dropdown">
              {sortOptions.map(option => (
                <div
                  key={option.value}
                  className={`sort-dropdown-item ${sortOption === option.value ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortOption(option.value);
                    setShowSortDropdown(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shop-header-separator"></div>

        {isFiltered && (
          <div className="shop-header-clear" onClick={handleClearAll}>
            <span>Clear</span>
          </div>
        )}

        <div className="shop-header-filter" onClick={() => setIsFilterDrawerOpen(true)}>
          <div className="filter-icon-container">
            <div className="filter-line-wrapper">
              <div className="filter-knob" style={{ left: '30%' }}></div>
            </div>
            <div className="filter-line-wrapper">
              <div className="filter-knob" style={{ left: '70%' }}></div>
            </div>
          </div>
          <span>Filter</span>
        </div>
      </div>

      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
      />
    </div>
  );
};

export default ShopHeader;
